// appointment.ts
import dayjs, { Dayjs } from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import { apiClient } from '../../apiHelper';

dayjs.extend(customParseFormat);

/** Naive wall-clock string — no timezone suffix (matches backend scheduling API). */
export const WALL_CLOCK_FORMAT = 'YYYY-MM-DDTHH:mm:ss';

/** Maps API/axios errors to a user-visible message (see apiHelper response interceptor). */
export function getApiErrorMessage(err: unknown, fallback: string): string {
  const e = err as {
    message?: string | { msg?: string };
    detail?: string | { msg?: string } | Array<{ msg?: string }>;
    response?: { data?: { message?: string; detail?: string } };
    data?: { message?: string; detail?: string };
  };
  const pick = (value: unknown): string | undefined => {
    if (typeof value === 'string' && value.trim()) return value.trim();
    if (Array.isArray(value) && value[0]?.msg) return String(value[0].msg);
    if (value && typeof value === 'object' && 'msg' in value) {
      const msg = (value as { msg?: string }).msg;
      return msg ? String(msg) : undefined;
    }
    return undefined;
  };
  return (
    pick(e?.message) ||
    pick(e?.detail) ||
    pick(e?.response?.data?.message) ||
    pick(e?.response?.data?.detail) ||
    pick(e?.data?.message) ||
    pick(e?.data?.detail) ||
    fallback
  );
}

function normalizeAppointmentStatus(status: string | undefined | null): string {
  return String(status || '')
    .toLowerCase()
    .replace(/^.*\./, '');
}

/** Appointment end as wall-clock (prefers end_time, else start + duration). */
export function getAppointmentEndTime(
  app: Pick<AppointmentRead, 'start_time' | 'end_time' | 'duration_minutes'>
): Dayjs {
  if (app.end_time) return parseAppointmentTime(app.end_time);
  return parseAppointmentTime(app.start_time).add(
    app.duration_minutes || 30,
    'minute'
  );
}

/** Scheduled visit whose slot has already ended without starting. */
export function isAppointmentPassed(
  app: Pick<
    AppointmentRead,
    'start_time' | 'end_time' | 'duration_minutes' | 'status'
  >
): boolean {
  const st = normalizeAppointmentStatus(app.status);
  if (st !== 'scheduled' && st !== 'confirmed') return false;
  return dayjs().isAfter(getAppointmentEndTime(app), 'minute');
}

/** In-progress visit whose scheduled duration/end time has elapsed. */
export function isInProgressPastEnd(
  app: Pick<
    AppointmentRead,
    'start_time' | 'end_time' | 'duration_minutes' | 'status'
  >
): boolean {
  const st = normalizeAppointmentStatus(app.status);
  if (st !== 'in_progress') return false;
  return dayjs().isAfter(getAppointmentEndTime(app), 'minute');
}

/** True when schedule fields can still be edited (not started / not finished). */
export function canModifyAppointment(
  app: Pick<
    AppointmentRead,
    'status' | 'start_time' | 'end_time' | 'duration_minutes'
  >
): boolean {
  const st = normalizeAppointmentStatus(app.status);
  if (st === 'in_progress' || st === 'completed' || st === 'finished') {
    return false;
  }
  if (isInProgressPastEnd(app)) return false;
  return st === 'scheduled' || st === 'confirmed';
}

/**
 * Start exam only during the scheduled window:
 * - in progress → resume allowed until the slot ends
 * - scheduled → only from start time through end time
 */
export function canStartExamNow(
  app: Pick<
    AppointmentRead,
    'start_time' | 'end_time' | 'duration_minutes' | 'status'
  >
): boolean {
  const st = normalizeAppointmentStatus(app.status);
  if (st === 'in_progress') return !isInProgressPastEnd(app);
  if (st !== 'scheduled' && st !== 'confirmed') return false;
  const now = dayjs();
  const start = parseAppointmentTime(app.start_time);
  const end = getAppointmentEndTime(app);
  return !now.isBefore(start, 'minute') && !now.isAfter(end, 'minute');
}

type AppointmentInterval = Pick<
  AppointmentRead,
  'start_time' | 'end_time' | 'duration_minutes' | 'status'
>;

/** Build a local wall-clock datetime (matches the schedule grid). */
export function buildLocalDateTime(date: string, time: string): Dayjs {
  return dayjs(`${date}T${time}`, 'YYYY-MM-DDTHH:mm');
}

/** Parse API appointment timestamps as clinic wall-clock (never UTC-shift). */
export function parseAppointmentTime(value: string): Dayjs {
  const naive = value.replace(/(\.\d+)?([+-]\d{2}:?\d{2}|Z)$/i, '');
  const strict = dayjs(naive, WALL_CLOCK_FORMAT, true);
  if (strict.isValid()) return strict;
  const short = dayjs(naive, 'YYYY-MM-DDTHH:mm', true);
  if (short.isValid()) return short;
  return dayjs(naive, WALL_CLOCK_FORMAT);
}

/** HH:mm label for grid rows and dropdowns. */
export function formatAppointmentClock(value: string): string {
  return parseAppointmentTime(value).format('HH:mm');
}

/** Build slot grid times for a calendar day. */
export function buildDaySlotTimes(
  date: string,
  startHour = 8,
  endHour = 19,
  stepMinutes = 30
): Dayjs[] {
  const slots: Dayjs[] = [];
  let curr = buildLocalDateTime(date, `${String(startHour).padStart(2, '0')}:00`);
  const end = buildLocalDateTime(date, `${String(endHour).padStart(2, '0')}:00`);
  while (curr.isBefore(end)) {
    slots.push(curr);
    curr = curr.add(stepMinutes, 'minute');
  }
  return slots;
}

/** Serialize for the API as naive wall-clock (backend applies client timezone). */
export function toApiDateTime(value: Dayjs): string {
  return value.format(WALL_CLOCK_FORMAT);
}

/** Deduplicate agenda rows and drop cancelled visits. */
export function dedupeAppointments(items: AppointmentRead[]): AppointmentRead[] {
  const seen = new Set<string>();
  return items.filter((item) => {
    if (!item?.id || item.status === 'cancelled') return false;
    if (seen.has(item.id)) return false;
    seen.add(item.id);
    return true;
  });
}

/** Authoritative interval: start + duration_minutes (matches backend storage). */
export function getAppointmentBounds(
  appointment: AppointmentInterval
): { start: Dayjs; end: Dayjs } | null {
  if (appointment.status === 'cancelled') return null;
  const start = parseAppointmentTime(appointment.start_time);
  if (!start.isValid()) return null;
  const duration = appointment.duration_minutes || 30;
  const end = start.add(duration, 'minute');
  return { start, end };
}

/** Appointment starts within this grid row [slotStart, slotEnd). */
export function appointmentStartsInSlot(
  slotStart: Dayjs,
  slotEnd: Dayjs,
  appointment: AppointmentInterval
): boolean {
  const bounds = getAppointmentBounds(appointment);
  if (!bounds) return false;
  return (
    !bounds.start.isBefore(slotStart) && bounds.start.isBefore(slotEnd)
  );
}

/** Visit runs through this row but started in an earlier slot. */
export function appointmentContinuesInSlot(
  slotStart: Dayjs,
  slotEnd: Dayjs,
  appointment: AppointmentInterval
): boolean {
  return (
    appointmentIntervalsOverlap(slotStart, slotEnd, appointment) &&
    !appointmentStartsInSlot(slotStart, slotEnd, appointment)
  );
}

/** How many grid rows a visit occupies at the given slot step. */
export function getAppointmentSlotSpan(
  appointment: AppointmentInterval,
  slotMinutes: number
): number {
  const step = Math.max(1, slotMinutes);
  const visitMinutes = appointment.duration_minutes || step;
  return Math.max(1, Math.ceil(visitMinutes / step));
}

/** Half-open overlap — used to detect patient double-booking. */
export function appointmentIntervalsOverlap(
  rangeStart: Dayjs,
  rangeEnd: Dayjs,
  appointment: AppointmentInterval
): boolean {
  const bounds = getAppointmentBounds(appointment);
  if (!bounds) return false;
  return rangeStart.isBefore(bounds.end) && rangeEnd.isAfter(bounds.start);
}

/** True when a proposed visit overlaps an existing one (half-open; back-to-back allowed). */
export function appointmentBlocksDoctorSlot(
  rangeStart: Dayjs,
  rangeEnd: Dayjs,
  appointment: AppointmentInterval
): boolean {
  return appointmentIntervalsOverlap(rangeStart, rangeEnd, appointment);
}

/** Same patient cannot have more than one active appointment on the same calendar day. */
export function patientHasScheduleConflict(
  date: string,
  patientId: string,
  appointments: Pick<
    AppointmentRead,
    'id' | 'patient_id' | 'start_time' | 'end_time' | 'duration_minutes' | 'status'
  >[],
  excludeAppointmentId?: string
): boolean {
  return appointments.some((app) => {
    if (excludeAppointmentId && app.id === excludeAppointmentId) return false;
    if (app.patient_id !== patientId) return false;
    if (app.status === 'cancelled') return false;
    const bounds = getAppointmentBounds(app);
    if (!bounds) return false;
    return bounds.start.format('YYYY-MM-DD') === date;
  });
}

/** True when a new visit of `durationMinutes` cannot start at `slotStart`. */
export function slotCannotStartVisit(
  slotStart: Dayjs,
  durationMinutes: number,
  appointments: AppointmentInterval[]
): boolean {
  const slotEnd = slotStart.add(durationMinutes, 'minute');
  return appointments.some((app) =>
    appointmentBlocksDoctorSlot(slotStart, slotEnd, app)
  );
}

export type AppointmentTypeValue =
  | 't1'
  | 't2'
  | 't3'
  | 'routine'
  | 'emergency'
  | 'gyn'
  | 'admin';

// Read interface (for fetching)
export interface AppointmentRead {
  id: string;
  patient_id: string;
  doctor_id?: string | null;
  created_by_id?: string | null;
  start_time: string;
  end_time: string;
  duration_minutes: number;
  appointment_type: AppointmentTypeValue | string;
  status:
    | 'draft'
    | 'scheduled'
    | 'in_progress'
    | 'completed'
    | 'no_show'
    | 'cancelled'
    | 'confirmed'
    | 'finished'
    | 'inProgress';
  priority: string;
  accession_number?: string | null;
  pregnancy_id?: number | null;
  reason?: string | null;
  source?: string | null;
  modality?: string | null;
  study_instance_uid?: string | null;
  /** Persisted Start Exam path: "ob" | "realtime" — Resume skips the choice modal. */
  exam_mode?: 'ob' | 'realtime' | string | null;
  scheduled_station_name?: string | null;
  scheduled_station_ae_title?: string | null;
  scheduled_procedure_step_location?: string | null;
  requested_procedure_id?: string | null;
  mwl_sync_status?: 'pending' | 'synced' | 'failed' | 'not_applicable' | null;
  mwl_sync_error?: string | null;
  mwl_order_id?: number | null;
  mwl_step_id?: number | null;
  facility_procedure_id?: number | null;
  patient_name?: string;
  patient_last_name?: string;
  patient_mrn?: string | null;
  patient_email?: string | null;
  patient_phone_number?: string | null;
  assignee_first_name?: string | null;
  assignee_last_name?: string | null;
  assignee_email?: string | null;
  booked_by_first_name?: string | null;
  booked_by_last_name?: string | null;
  booked_by_email?: string | null;
  ordering_provider_first_name?: string | null;
  ordering_provider_last_name?: string | null;
  ordering_provider_email?: string | null;
}

export interface AppointmentListFilters {
  status?: string[];
  appointment_type?: AppointmentTypeValue | string;
  performing_provider_id?: string;
  date_from?: string;
  date_to?: string;
  priority?: string;
  search?: string;
}

export interface BookableDoctor {
  id: string;
  first_name?: string | null;
  last_name?: string | null;
  email?: string | null;
  role?: string;
}

export interface AppointmentCreate {
  patient_id: string;
  doctor_id: string;
  start_time: string;
  end_time: string;
  duration_minutes: number;
  appointment_type: AppointmentTypeValue | string;
  reason?: string;
  priority?: string;
  status?: string;
  ordering_provider_id?: string;
  facility_procedure_id?: number | null;
  pregnancy_id?: number | null;
}

function unwrapList<T>(response: unknown): T[] {
  const body = response as { data?: T[] | { data?: T[] } };
  const nested = body?.data;
  if (Array.isArray(nested)) return nested;
  if (nested && Array.isArray((nested as { data?: T[] }).data)) {
    return (nested as { data: T[] }).data;
  }
  return Array.isArray(response) ? (response as T[]) : [];
}

/** Build `/realtimeexam` URL — only appointment id; page loads the rest from API. */
export function buildRealtimeExamPath(
  app: Pick<AppointmentRead, 'id'>
): string {
  return `/realtimeexam?appointmentId=${encodeURIComponent(String(app.id))}`;
}

export const appointmentService = {
  get: async (id: string): Promise<AppointmentRead> => {
    const response = await apiClient.get(`/appointments/${id}`);
    const body = response as { data?: AppointmentRead } | AppointmentRead;
    if (body && typeof body === 'object' && 'data' in body && body.data) {
      return body.data as AppointmentRead;
    }
    return body as AppointmentRead;
  },

  getDailyAgenda: async (
    startDate: string,
    endDate?: string,
    doctorId?: string
  ): Promise<AppointmentRead[]> => {
    const params: Record<string, string> = {
      start_date: startDate,
      end_date: endDate || startDate,
    };
    if (doctorId) params.doctor_id = doctorId;

    const response = await apiClient.get(`/appointments/agenda`, params);
    return unwrapList<AppointmentRead>(response);
  },

  list: async (filters: AppointmentListFilters = {}): Promise<AppointmentRead[]> => {
    const params: Record<string, string | string[]> = {};
    if (filters.status?.length) params.status = filters.status;
    if (filters.appointment_type) params.appointment_type = filters.appointment_type;
    if (filters.performing_provider_id) {
      params.performing_provider_id = filters.performing_provider_id;
    }
    if (filters.date_from) params.date_from = filters.date_from;
    if (filters.date_to) params.date_to = filters.date_to;
    if (filters.priority) params.priority = filters.priority;
    if (filters.search) params.search = filters.search;

    const response = await apiClient.get('/appointments', params);
    return unwrapList<AppointmentRead>(response);
  },

  getBookableDoctors: async (): Promise<BookableDoctor[]> => {
    const response = await apiClient.get('/appointments/bookable-doctors');
    const data = (response as { data?: BookableDoctor[] })?.data ?? response ?? [];
    return Array.isArray(data) ? data : [];
  },

  create: async (payload: AppointmentCreate): Promise<AppointmentRead> => {
    const response = await apiClient.post('/appointments', payload);
    return response as any as AppointmentRead;
  },

  update: async (id: string, data: Partial<AppointmentRead>) => {
    const response = await apiClient.patch(`/appointments/${id}`, data);
    return response as any;
  },

  delete: async (id: string) => {
    const response = await apiClient.delete(`/appointments/${id}`);
    return response as any;
  },

  cancel: async (id: string) => {
    const response = await apiClient.post(`/appointments/${id}/cancel`, {});
    return response as any;
  },

  checkIn: async (id: string) => {
    const response = await apiClient.post(`/appointments/${id}/check-in`, {});
    return response as any;
  },

  complete: async (id: string) => {
    const response = await apiClient.post(`/appointments/${id}/complete`, {});
    return response as any;
  },

  noShow: async (id: string) => {
    const response = await apiClient.post(`/appointments/${id}/no-show`, {});
    return response as any;
  },

  importMwl: async (payload: {
    destination_ae_id?: number;
    server_id?: number;
    items: Record<string, unknown>[];
    performing_provider_id?: string;
    default_duration_minutes?: number;
  }) => {
    const response = await apiClient.post('/appointments/import-mwl', payload);
    return response as any;
  },
};
